import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { RootState } from "redux/store";
import { Dialog } from "component/shared/dialog";
import { FeatherIcon } from "component/shared/feather-icon";
import { LoadingIndicator } from "component/shared/loading-indicator";
import { getSearchAgentConversationMessages } from "redux/selector";
import { fetchLeadConversationHistoryDay } from "redux/slice/search-agent";
import { SearchAgentConversationDay } from "model/search-agent-profile";
import { LeadProfileSuggestion } from "model/lead-profile-suggestion";
import { SparkleIcon } from "../icons";
import { AiSessionIds } from "../ai-session-ids";
import { formatLeadProfileSuggestionValue } from "../suggested-profile-updates-card/format";
import { styles } from "./style";

/**
 * The `YYYY-MM-DD` key a day is both stored and requested under.
 *
 * The day resource carries this as a date-only string and the selector turns it into a Moment.
 * Round-tripping it is lossless in any timezone — moment parses a date-only string as local
 * midnight and formats the same calendar date back — whereas deriving the key from the day's
 * `firstMessageOn` would not be, since that is a real instant and the backend buckets days in
 * Pacific time.
 */
const dateKey = (conversationDay: SearchAgentConversationDay) => {
	return conversationDay.date.format("YYYY-MM-DD");
};

const mapStateToProps = (
	state: RootState,
	ownProps: { leadId: number, conversationDay: SearchAgentConversationDay }
) => {
	return {
		messages: getSearchAgentConversationMessages(
			state,
			ownProps.leadId,
			dateKey(ownProps.conversationDay)
		),
	};
};

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	fetchLeadConversationHistoryDay,
}, dispatch);

interface Props extends
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	WithStyles<typeof styles> {
	open: boolean;
	onClose: () => void;
	leadId: number;
	conversationDay: SearchAgentConversationDay;
	/**
	 * A suggestion to offer above the transcript, with Accept and Dismiss. Each callback resolves
	 * true when the action succeeded, which closes the dialog; on failure it stays open, since
	 * the caller has reported the error and the suggestion is still pending.
	 */
	suggestion?: LeadProfileSuggestion;
	onAcceptSuggestion?: (suggestion: LeadProfileSuggestion) => Promise<boolean>;
	onDismissSuggestion?: (suggestion: LeadProfileSuggestion) => Promise<boolean>;
}

interface State {
	suggestionBusy: boolean;
}

class Component extends React.Component<Props, State> {

	public readonly state: State = {
		suggestionBusy: false,
	};

	public componentDidMount() {
		const { messages, leadId, conversationDay } = this.props;
		// Already-read days stay in redux, so reopening one costs no request. The tab mounts this
		// keyed by day and only while a day is selected, so mounting is the only time to ask.
		if (messages === undefined) {
			this.props.fetchLeadConversationHistoryDay({ leadId, date: dateKey(conversationDay) });
		}
	}

	public render() {
		const { open, onClose, conversationDay, classes = {} } = this.props;
		return (
			<Dialog open={open} onClose={onClose} scroll="paper">
				<Mui.DialogTitle className={classes.title}>
					<SparkleIcon className={classes.titleIcon} />
					{conversationDay.date.format("MMM D, YYYY")}
					<AiSessionIds aiSessionIds={conversationDay.aiSessionIds} />
				</Mui.DialogTitle>
				<Mui.IconButton
					className={classes.closeButton}
					onClick={onClose}
					aria-label="Close"
					size="small"
				>
					<FeatherIcon><Icons.X /></FeatherIcon>
				</Mui.IconButton>
				{this.renderSuggestion()}
				<Mui.DialogContent className={classes.content}>
					{this.renderBody()}
				</Mui.DialogContent>
			</Dialog>
		);
	}

	private renderSuggestion() {
		const { suggestion, onAcceptSuggestion, onDismissSuggestion, classes = {} } = this.props;
		const { suggestionBusy } = this.state;
		if (!suggestion || !onAcceptSuggestion || !onDismissSuggestion) {
			return null;
		}
		const { field } = suggestion;
		return (
			<div className={classes.suggestion}>
				<div className={classes.suggestionText}>
					<SparkleIcon className={classes.suggestionIcon} />
					<span>
						<span className={classes.suggestionHeading}>Suggested update</span>
						{" · "}
						{field.label}: {formatLeadProfileSuggestionValue(field, suggestion.current)}
						{" → "}
						{formatLeadProfileSuggestionValue(field, suggestion.suggested)}
					</span>
				</div>
				<div className={classes.suggestionActions}>
					<Mui.Button
						size="small"
						variant="outlined"
						color="primary"
						disabled={suggestionBusy}
						onClick={() => this.handleSuggestionAction(onDismissSuggestion)}
					>
						Dismiss
					</Mui.Button>
					<Mui.Button
						size="small"
						variant="contained"
						color="primary"
						disabled={suggestionBusy}
						onClick={() => this.handleSuggestionAction(onAcceptSuggestion)}
					>
						Accept
					</Mui.Button>
				</div>
			</div>
		);
	}

	private handleSuggestionAction = async (action: (suggestion: LeadProfileSuggestion) => Promise<boolean>) => {
		const { suggestion, onClose } = this.props;
		if (!suggestion) {
			return;
		}
		this.setState({ suggestionBusy: true });
		const succeeded = await action(suggestion);
		if (succeeded) {
			onClose();
		} else {
			this.setState({ suggestionBusy: false });
		}
	};

	/**
	 * The day's messages are keyed by date in redux, so a response that arrives after the reader
	 * has moved to another day lands under its own date and never reaches the screen. That makes
	 * "not fetched yet" the only loading condition worth rendering.
	 */
	private renderBody() {
		const { messages, classes = {} } = this.props;
		if (messages === undefined) {
			return (
				<Mui.Grid container justifyContent="center">
					<Mui.Grid item>
						<LoadingIndicator />
					</Mui.Grid>
				</Mui.Grid>
			);
		}
		if (!messages.length) {
			return (
				<Mui.Typography className={classes.empty}>
					There are no messages to show for this day.
				</Mui.Typography>
			);
		}
		return messages.map((message) => {
			const isLead = message.role === "human";
			return (
				<div
					key={message.id}
					className={`${classes.row} ${isLead ? classes.rowLead : ""}`}
				>
					<div className={`${classes.avatar} ${isLead ? classes.avatarLead : classes.avatarAgent}`}>
						{isLead ? (
							<FeatherIcon className={classes.avatarIcon}><Icons.User /></FeatherIcon>
						) : (
							<SparkleIcon className={classes.avatarIcon} />
						)}
					</div>
					<div className={`${classes.column} ${isLead ? classes.columnLead : ""}`}>
						<div className={`${classes.bubble} ${isLead ? classes.bubbleLead : classes.bubbleAgent}`}>
							{message.content}
						</div>
						<span className={classes.time}>{message.createdOn.format("h:mm A")}</span>
					</div>
				</div>
			);
		});
	}
}

export const ConversationTranscriptDialog = withStyles(
	connect(mapStateToProps, mapDispatchToProps)(Component),
	styles
);
